import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { StructuredText } from 'react-datocms';

const DISMISS_KEY = 'announcementDismissedAt';
const CSS_VAR = '--announcement-bar-height';
const SHOW_DELAY = 1000;
const ANIM_MS = 400;

const AnnouncementBar = ({ data }) => {
	const { content, background, time } = data || {};
	const DISMISS_TTL = (time | 10) * 60 * 1000; // Defaults to 10 Minutes

	const barRef = useRef(null);
	const [isMounted, setIsMounted] = useState(false);
	const [isVisible, setIsVisible] = useState(false);
	const [isDismissed, setIsDismissed] = useState(false);

	const setCssHeight = (value) => {
		document.documentElement.style.setProperty(CSS_VAR, `${value}px`);
	};

	const isDismissValid = () => {
		const raw = localStorage.getItem(DISMISS_KEY);
		if (!raw) return false;

		const dismissedAt = Number(raw);
		if (Number.isNaN(dismissedAt)) return false;

		return Date.now() - dismissedAt < DISMISS_TTL;
	};

	// Initial show logic (delayed) + 1 hour dismissal TTL
	useEffect(() => {
		if (!data) {
			setCssHeight(0);
			return;
		}

		// If previously dismissed within TTL, don't show
		if (isDismissValid()) {
			setIsDismissed(true);
			setCssHeight(0);
			return;
		}

		const t = window.setTimeout(() => {
			setIsMounted(true);
			requestAnimationFrame(() => setIsVisible(true));
		}, SHOW_DELAY);

		return () => window.clearTimeout(t);
	}, [data]);

	// Measure height and sync to CSS var
	useLayoutEffect(() => {
		if (!barRef.current) return;

		const el = barRef.current;

		const report = () => {
			const height = isVisible ? el.getBoundingClientRect().height : 0;
			setCssHeight(Math.ceil(height));
		};

		report();

		const ro = new ResizeObserver(report);
		ro.observe(el);

		return () => ro.disconnect();
	}, [isVisible, content]);

	const dismissBar = () => {
		setIsVisible(false);
		localStorage.setItem(DISMISS_KEY, Date.now().toString());

		// Optional: fully unmount after animation
		window.setTimeout(() => {
			setIsDismissed(true);
			setCssHeight(0);
		}, ANIM_MS);
	};

	// Safe early exits (hooks already ran)
	if (!data) return null;
	if (isDismissed) return null;
	if (!isMounted) return null;

	return (
		<div
			ref={barRef}
			className={`announcement-bar background-${background} ${
				isVisible ? 'is-visible' : 'is-hidden'
			}`}
			role='region'
			aria-label='Announcement'
		>
			<div className='announcement-bar__inner'>
				<div className='announcement-bar__content'>
					<StructuredText data={content} />
				</div>
				<button
					type='button'
					className='announcement-bar__close'
					onClick={dismissBar}
					aria-label='Dismiss announcement'
				>
					×
				</button>
			</div>
		</div>
	);
};

export default AnnouncementBar;
